/**
 * Split an interval that spans multiple calendar days (in a given
 * timezone) into per-day pieces.
 *
 *   Input : Mon 14:00 → Wed 10:00  (Asia/Kolkata)
 *   Output: Mon 14:00 → Mon 23:59:59.999
 *           Tue 00:00 → Tue 23:59:59.999
 *           Wed 00:00 → Wed 10:00
 *
 * Day keys are returned as `YYYY-MM-DD` in the target timezone, so
 * downstream code can index events by day without re-parsing.
 */
import moment from 'moment-timezone';
import type { Interval } from './types';

export interface DayPiece extends Interval {
  dateKey: string;
}

export function splitIntervalByDay(
  interval: Interval,
  timezone: string
): DayPiece[] {
  const start = moment.tz(interval.start, timezone);
  const end = moment.tz(interval.end, timezone);
  if (!start.isValid() || !end.isValid() || start.isSameOrAfter(end)) {
    return [];
  }

  const pieces: DayPiece[] = [];
  let cursor = start.clone();
  while (cursor.isBefore(end)) {
    const endOfDay = cursor.clone().endOf('day');
    const pieceEnd = moment.min(end, endOfDay);
    pieces.push({
      dateKey: cursor.format('YYYY-MM-DD'),
      start: cursor.toISOString(),
      end: pieceEnd.toISOString(),
    });
    cursor = endOfDay.clone().add(1, 'millisecond'); // jump to next day's 00:00
  }
  return pieces;
}
